/**
 * MaterialItem — status-gedreven paklijst-rij (Stitch design)
 * Rij-klik togglet 'Gepakt'. Status-chip opent de status-popover, prullenbak verwijdert.
 */
import React, { useState } from 'react';
import { Box, Typography, Tooltip } from '@mui/material';
import { MaterialVisual } from '../../pages/MateriaalBeheer/IconColorPicker';

const T = {
  primary: '#526075', surface: '#ffffff', surfaceLow: '#f1f4f5', surfaceHigh: '#e5e9eb',
  text: '#2d3335', secondary: '#556071', outline: '#767c7e', outlineVariant: '#adb3b5',
  error: '#a83836', errorBg: '#fef2f2',
  emerald: '#10b981', emeraldBg: '#ecfdf5',
  gold: '#d4943a', goldBg: '#fbf3e6',
  fontBody: '"Inter", system-ui, sans-serif',
  font: '"Manrope", system-ui, sans-serif',
  shadow: '0 1px 3px rgba(45,51,53,0.04)',
};

const Icon = ({ name, size = 24, sx = {} }) => (
  <span className="material-symbols-outlined" style={{ fontSize: size, fontVariationSettings: "'FILL' 0, 'wght' 400", ...sx }}>{name}</span>
);

const MaterialItem = ({ material, onToggleGepakt, onOpenStatus, onRemove, typeById = {}, onOpenTypeInfo }) => {
  const [confirming, setConfirming] = useState(false);

  const rawType = material?.fields?.MateriaalType;
  const typeId = Array.isArray(rawType) ? rawType[0] : rawType;
  const type = typeId ? typeById[typeId] : null;
  const tIcon = type?.fields?.Icoon;
  const tColor = type?.fields?.Kleur;
  const noReserv = !!type?.fields?.GeenReserveringNodig;

  const statuses = material?.fields?.JunctionStatus || [];
  const isGepakt = statuses.includes('Gepakt');
  const isNogPakken = statuses.includes('Nog pakken');
  const isIngehuurd = statuses.includes('Ingehuurd');

  const accent = isGepakt ? T.emerald : isNogPakken ? T.error : T.outlineVariant;
  const tint = isGepakt ? T.emeraldBg : isNogPakken ? T.errorBg : T.surface;
  const borderCol = isGepakt ? `${T.emerald}30` : isNogPakken ? `${T.error}25` : 'rgba(45,51,53,0.08)';

  const stop = (e) => e.stopPropagation();

  return (
    <Box onClick={() => onToggleGepakt(material)} sx={{
      position: 'relative', display: 'flex', alignItems: 'center', gap: 0.75,
      bgcolor: tint, borderRadius: '0.6rem',
      border: `1px solid ${borderCol}`,
      pl: 1.25, pr: 0.6, py: 0.9, cursor: 'pointer', transition: 'all 0.15s', boxShadow: T.shadow,
      '&:hover': { boxShadow: '0 3px 10px rgba(45,51,53,0.08)' },
      '&:hover .row-trash': { opacity: 1 },
    }}>
      {/* Gepakt-check */}
      <Tooltip title={isGepakt ? 'Markeer als niet gepakt' : 'Markeer als gepakt'} arrow>
        <Box sx={{
          width: 24, height: 24, borderRadius: '50%', flexShrink: 0,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          border: `2px solid ${isGepakt ? T.emerald : T.outlineVariant}`,
          bgcolor: isGepakt ? T.emerald : 'transparent', transition: 'all 0.15s',
        }}>
          {isGepakt && <Icon name="check" size={15} sx={{ color: '#fff' }} />}
        </Box>
      </Tooltip>

      {/* Icoon/kleur van het materiaal */}
      {(tIcon || tColor) && (
        <Box sx={{ width: 22, height: 22, borderRadius: '0.35rem', flexShrink: 0, overflow: 'hidden' }}>
          <MaterialVisual icon={tIcon} color={tColor} fallbackIcon="inventory_2" iconSize={14} />
        </Box>
      )}

      {/* Naam + 'Geen reservering nodig' */}
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography sx={{ fontFamily: T.fontBody, fontSize: '0.82rem', fontWeight: 600, color: isGepakt ? T.outline : T.text, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {material?.fields?.ID || 'Onbekend'}
        </Typography>
        {noReserv && (
          <Tooltip title="Genoeg in magazijn — dit materiaal geeft geen reserveringsconflicten" arrow>
            <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.3, mt: 0.15, px: 0.5, py: 0.1, borderRadius: '0.3rem', bgcolor: '#fef3c7', border: '1px solid #f59e0b40' }}>
              <span className="material-symbols-outlined" style={{ fontSize: 11, color: '#d4943a' }}>all_inclusive</span>
              <Typography sx={{ fontFamily: T.fontBody, fontSize: '0.55rem', fontWeight: 800, color: '#d4943a' }}>Geen reservering nodig</Typography>
            </Box>
          </Tooltip>
        )}
      </Box>

      {/* Info-knop → compleet materiaal */}
      {onOpenTypeInfo && (
        <Tooltip title="Materiaal-info" arrow>
          <Box onClick={(e) => { stop(e); onOpenTypeInfo(material); }} sx={{ flexShrink: 0, width: 24, height: 24, borderRadius: '0.35rem', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', color: T.outline, '&:hover': { bgcolor: T.surfaceLow, color: T.primary } }}>
            <Icon name="info" size={16} />
          </Box>
        </Tooltip>
      )}

      {/* Ingehuurd-indicator */}
      {isIngehuurd && (
        <Tooltip title="Ingehuurd" arrow>
          <Box sx={{ flexShrink: 0, display: 'inline-flex', alignItems: 'center', gap: 0.3, px: 0.55, py: 0.15, borderRadius: '0.35rem', bgcolor: T.goldBg, border: `1px solid ${T.gold}40` }}>
            <Icon name="local_shipping" size={12} sx={{ color: T.gold }} />
            <Typography sx={{ fontFamily: T.fontBody, fontSize: '0.58rem', fontWeight: 800, color: T.gold, letterSpacing: '0.02em' }}>Huur</Typography>
          </Box>
        </Tooltip>
      )}

      {/* Status-chip → popover */}
      <Tooltip title="Status aanpassen" arrow>
        <Box onClick={(e) => { stop(e); onOpenStatus(e, material); }} sx={{
          flexShrink: 0, display: 'inline-flex', alignItems: 'center', gap: 0.35, px: 0.85, py: 0.5, borderRadius: '0.4rem',
          cursor: 'pointer', border: `1px solid ${T.surfaceHigh}`, bgcolor: T.surface, transition: 'all 0.15s',
          '&:hover': { bgcolor: T.surfaceLow, borderColor: T.outlineVariant },
        }}>
          <Box sx={{ width: 7, height: 7, borderRadius: '50%', bgcolor: accent, flexShrink: 0 }} />
          <Typography sx={{ fontFamily: T.fontBody, fontSize: '0.64rem', fontWeight: 700, color: T.secondary, whiteSpace: 'nowrap' }}>
            {isGepakt ? 'Gepakt' : isNogPakken ? 'Nog pakken' : 'Status'}
          </Typography>
          <Icon name="expand_more" size={14} sx={{ color: T.outlineVariant }} />
        </Box>
      </Tooltip>

      {/* Verwijderen */}
      {confirming ? (
        <Box onClick={stop} sx={{ flexShrink: 0, display: 'inline-flex', alignItems: 'center', gap: 0.25 }}>
          <Tooltip title="Verwijderen uit reservering" arrow>
            <Box onClick={(e) => { stop(e); onRemove(material); setConfirming(false); }} sx={{
              width: 26, height: 26, borderRadius: '0.35rem', display: 'flex', alignItems: 'center', justifyContent: 'center',
              cursor: 'pointer', bgcolor: T.error, '&:hover': { filter: 'brightness(0.92)' },
            }}>
              <Icon name="check" size={15} sx={{ color: '#fff' }} />
            </Box>
          </Tooltip>
          <Tooltip title="Annuleren" arrow>
            <Box onClick={(e) => { stop(e); setConfirming(false); }} sx={{
              width: 26, height: 26, borderRadius: '0.35rem', display: 'flex', alignItems: 'center', justifyContent: 'center',
              cursor: 'pointer', '&:hover': { bgcolor: T.surfaceLow },
            }}>
              <Icon name="close" size={15} sx={{ color: T.outline }} />
            </Box>
          </Tooltip>
        </Box>
      ) : (
        <Tooltip title="Verwijderen uit reservering" arrow>
          <Box className="row-trash" onClick={(e) => { stop(e); setConfirming(true); }} sx={{
            flexShrink: 0, opacity: 0.4, transition: 'opacity 0.15s, background-color 0.15s', cursor: 'pointer',
            width: 26, height: 26, borderRadius: '0.35rem', display: 'flex', alignItems: 'center', justifyContent: 'center',
            '&:hover': { bgcolor: T.errorBg, opacity: 1 },
          }}>
            <Icon name="delete" size={15} sx={{ color: T.outline }} />
          </Box>
        </Tooltip>
      )}
    </Box>
  );
};

export default MaterialItem;
